import { STEP_UP_OAUTH_CONFIG, STEP_UP_POPUP_BLOCKED_CODE } from './constants';
import { PKCE_CODE_CHALLENGE_METHOD } from './pkce';
import { StepUpCancelledError, StepUpFailedError } from './stepUpError';
import type {
  AuthorizationResult,
  StepUpOAuthConfig,
  StepUpRequiredDetails,
  WebMessageResponse,
} from './types';

type Settlers = {
  resolve: (result: AuthorizationResult) => void;
  reject: (error: Error) => void;
};

// Config the popup needs (authorize URL + OAuth params, incl. redirect_uri).
// `tokenUrl` lives on StepUpOAuthConfig too but is used by the code→token exchange.
export type StepUpPopupConfig = Pick<
  StepUpOAuthConfig,
  'authorizeUrl' | 'redirectUri' | 'clientId' | 'scope'
> & {
  // Keep the popup window OPEN when authorize resolves successfully, instead of
  // closing it — for flows that hand the same window on to a next page (e.g. a
  // code-handoff to another page). Failures/cancellations still close the window.
  // Claim the kept window with takeWindow(). Default: false (close on any settle).
  keepOpenOnResolve?: boolean;
};

export const DEFAULT_STEP_UP_POPUP_CONFIG: StepUpPopupConfig = {
  authorizeUrl: STEP_UP_OAUTH_CONFIG.AUTHORIZE_URL,
  redirectUri: STEP_UP_OAUTH_CONFIG.REDIRECT_URI,
  clientId: STEP_UP_OAUTH_CONFIG.CLIENT_ID,
  scope: STEP_UP_OAUTH_CONFIG.SCOPE,
};

// Drives oauth2ng's `/authorize` (response_mode=web_message) popup for a single
// step-up flow and resolves with the OAuth authorization code. oauth2ng's
// `usersWebMessageHtml` page posts `{ code, state, error }` to the opener
// (server-infra PR #65059); we accept it only when the SENDER origin is the
// authorize endpoint's origin and the echoed `state` matches the one we issued.
//
// TWO-PHASE by design, to survive popup blockers: `open()` is SYNCHRONOUS and must
// run inside the user gesture (click) — it opens a blank popup right away, before
// any `await`. The caller then computes the PKCE challenge (async) off the gesture
// and calls `authorize()` to navigate the already-open window to `/authorize`.
// (Browsers only allow `window.open` synchronously within a user gesture.)
//
// Rejects with StepUpFailedError if the popup is blocked / reports an error, or
// StepUpCancelledError if the user closes the popup or nothing valid arrives before
// the timeout. Two safety nets: a best-effort `popup.closed` poll settles a
// user-closed popup promptly (important because minting is single-flight per acr — a
// hung flow would otherwise stall every step-up for that acr), and an overall
// timeout backstops any environment where the handle is neutered.
export class StepUpPopup {
  private popupWindow: Window | null = null;
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private closeCheckId: ReturnType<typeof setInterval> | null = null;
  private settled = false;
  private state: string | null = null;
  private settlers: Settlers | null = null;
  private readonly config: StepUpPopupConfig;

  constructor(config: StepUpPopupConfig = DEFAULT_STEP_UP_POPUP_CONFIG) {
    this.config = config;
  }

  // Phase 1 — SYNCHRONOUS, call inside the click gesture. Opens a BLANK popup (so it
  // is not caught by popup blockers), starts listening for the web_message, arms the
  // timeout + close-poll, and returns the promise that later settles with the
  // authorization result. Rejects immediately (synchronously-settled promise) if the
  // popup is blocked or OAuth is not configured. `authorize()` then navigates it.
  open(): Promise<AuthorizationResult> {
    // Reject any still-pending flow on this instance first, so a re-entrant call
    // never orphans the previous caller's promise (reset() alone wouldn't settle it).
    this.settle((s) => s.reject(new StepUpCancelledError()));
    this.reset();
    this.settled = false;
    this.state = crypto.randomUUID();

    return new Promise<AuthorizationResult>((resolve, reject) => {
      this.settlers = { resolve, reject };

      if (!this.config.authorizeUrl || !this.config.clientId) {
        // Fail loudly rather than opening a popup we could never navigate. clientId
        // is required per consumer (see constants); there is no shared default.
        this.settle((s) =>
          s.reject(
            new StepUpFailedError(
              undefined,
              'Step-up OAuth is not configured (authorizeUrl / clientId missing)',
            ),
          ),
        );
        return;
      }

      this.popupWindow = window.open(
        // Blank for now — authorize() navigates it once the PKCE challenge is ready.
        // Opening blank here (not the authorize URL) is what lets this run
        // synchronously in the gesture without first awaiting PKCE.
        '',
        // Per-flow window name: concurrent step-ups for different ACRs must not share
        // one named window (a second window.open would navigate the first away).
        `${STEP_UP_OAUTH_CONFIG.WINDOW_NAME}-${this.state}`,
        this.buildPopupFeatures(),
      );

      if (!this.popupWindow) {
        // Dedicated code: recoverable by the user (allow popups / retry from a fresh
        // click) and separately counted in telemetry.
        this.settle((s) =>
          s.reject(
            new StepUpFailedError(
              STEP_UP_POPUP_BLOCKED_CODE,
              'Step-up popup was blocked',
            ),
          ),
        );
        return;
      }

      window.addEventListener('message', this.handleMessageListener);
      this.timeoutId = setTimeout(() => {
        this.settle((s) => s.reject(new StepUpCancelledError()));
      }, STEP_UP_OAUTH_CONFIG.TIMEOUT_MS);
      // Best-effort: if the user closes the popup, settle as cancelled promptly
      // rather than waiting out TIMEOUT_MS. `window.closed` stays readable even after
      // the popup navigates cross-origin.
      this.closeCheckId = setInterval(() => {
        if (this.popupWindow?.closed) {
          this.settle((s) => s.reject(new StepUpCancelledError()));
        }
      }, STEP_UP_OAUTH_CONFIG.CLOSE_CHECK_INTERVAL_MS);
    });
  }

  // Phase 2 — navigate the popup opened by open() to `/authorize` with the PKCE S256
  // `codeChallenge` (the caller keeps the matching verifier for the token exchange).
  // No-op if the flow already settled (e.g. the popup was blocked in open()) or
  // open() was never called; the outcome is still delivered via open()'s promise.
  authorize(details: StepUpRequiredDetails, codeChallenge: string): void {
    if (this.settled || !this.popupWindow || !this.state) {
      return;
    }
    this.popupWindow.location.href = this.buildAuthorizeUrl(
      this.state,
      details,
      codeChallenge,
    );
  }

  // Tears down the popup, listener and timers, rejecting a pending flow as cancelled.
  // Safe to call multiple times.
  close(): void {
    this.settle((s) => s.reject(new StepUpCancelledError()));
    this.reset();
  }

  // Claim the (kept-open) popup window and relinquish ownership: after this, close()
  // and reset() will not touch it — the caller owns its lifecycle. Null when the
  // window was closed, never opened, or already taken.
  takeWindow(): Window | null {
    const popupWindow = this.popupWindow;
    this.popupWindow = null;
    return popupWindow && !popupWindow.closed ? popupWindow : null;
  }

  private buildAuthorizeUrl(
    state: string,
    details: StepUpRequiredDetails,
    codeChallenge: string,
  ): string {
    const params = new URLSearchParams({
      client_id: this.config.clientId,
      response_type: STEP_UP_OAUTH_CONFIG.RESPONSE_TYPE,
      response_mode: STEP_UP_OAUTH_CONFIG.RESPONSE_MODE,
      // Defaults to the current origin; overridable via config. Must match /token.
      redirect_uri: this.config.redirectUri || window.location.origin,
      scope: this.config.scope,
      state,
      code_challenge: codeChallenge,
      code_challenge_method: PKCE_CODE_CHALLENGE_METHOD,
    });
    // OIDC acr_values is a space-separated OR-list; omitted for a max_age-only step-up.
    if (details.acrValues.length) {
      params.set('acr_values', details.acrValues.join(' '));
    }
    if (details.maxAge != null) {
      params.set('max_age', String(details.maxAge));
    }
    return `${this.config.authorizeUrl}?${params.toString()}`;
  }

  private buildPopupFeatures(): string {
    const { WIDTH, HEIGHT } = STEP_UP_OAUTH_CONFIG;
    const left = window.screenX + Math.round((window.outerWidth - WIDTH) / 2);
    const top = window.screenY + Math.round((window.outerHeight - HEIGHT) / 2);
    return `width=${WIDTH},height=${HEIGHT},left=${left},top=${top},scrollbars=yes,resizable=yes`;
  }

  // The message SENDER is oauth2ng's web_message page, served from the authorize
  // endpoint's origin — so that is the origin we require.
  private get expectedOrigin(): string {
    try {
      return new URL(this.config.authorizeUrl).origin;
    } catch {
      return '';
    }
  }

  private handleMessageListener = (event: MessageEvent): void => {
    if (!this.expectedOrigin || event.origin !== this.expectedOrigin) {
      return;
    }
    const data = event.data as Partial<WebMessageResponse> | undefined;
    if (!data || typeof data !== 'object') {
      return;
    }
    // Strict state check (CSRF): a mismatch means the message is not for this flow.
    if (typeof data.state !== 'string' || data.state !== this.state) {
      return;
    }
    if (data.error) {
      this.settle((s) => s.reject(new StepUpFailedError(data.error)));
      return;
    }
    if (typeof data.code === 'string' && data.code) {
      const { code, state } = data as WebMessageResponse;
      // Success is the one settle that may keep the window (keepOpenOnResolve) so the
      // caller can hand it on to the next page.
      this.settle(
        (s) => s.resolve({ code, state }),
        Boolean(this.config.keepOpenOnResolve),
      );
    }
    // Neither code nor error for our state: ignore and keep waiting.
  };

  // Resolves/rejects the pending promise exactly once, then tears down. keepWindow
  // leaves the popup open (and owned by this instance until takeWindow()).
  private settle(fn: (settlers: Settlers) => void, keepWindow = false): void {
    if (this.settled || !this.settlers) {
      return;
    }
    this.settled = true;
    const settlers = this.settlers;
    this.reset(keepWindow);
    fn(settlers);
  }

  private reset(keepWindow = false): void {
    window.removeEventListener('message', this.handleMessageListener);
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.closeCheckId !== null) {
      clearInterval(this.closeCheckId);
      this.closeCheckId = null;
    }
    if (keepWindow) {
      // Listeners/timers are gone; the window handle stays for takeWindow().
      this.settlers = null;
      return;
    }
    if (this.popupWindow && !this.popupWindow.closed) {
      this.popupWindow.close();
    }
    this.popupWindow = null;
    this.settlers = null;
  }
}
